import React, { useRef, useEffect, useState as useReactState } from 'react';
import { useEnvironment, useExperiments } from '@wix/yoshi-flow-editor';
import s from './ProductImage.scss';
import cx from 'classnames';
import { DefaultImageIcon } from './DefaultImageIcon';
import {
  Image,
  ImageLoadingBehaviorOptions,
  ImageHoverEffectOptions,
  ImageResizeOptions,
} from '@wix/wix-ui-tpa';
import { useControllerProps } from '../../ControllerContext';
import { useState } from '../../stores';
import {
  DEFAULT_IMAGE_WIDTH_DESKTOP,
  DEFAULT_IMAGE_WIDTH_MOBILE,
  SPECS,
} from '../../../../../consts';
import { useStyles } from '@wix/tpa-settings/react';
import stylesParams from '../../../stylesParams';
import { convertRatioParamToRatioValue } from '../../../Settings/Tabs/DesignTab/utils';

export enum ProductImageDataHook {
  image = 'ProductImageDataHook.image',
  defaultImage = 'ProductImageDataHook.defaultImage',
}
const ProductImage = () => {
  const styles = useStyles();
  const {
    state: { checkout },
  } = useState();
  const { isMobile } = useEnvironment();
  const { product, presetVariants } = useControllerProps().giftCardStore;
  const { experiments } = useExperiments();
  const selectedVariant = presetVariants.find(
    (variant) => variant.id === checkout.purchaseOptions.variantId,
  );
  const containerRef = useRef<HTMLDivElement>(null);

  const shouldUseNewGiftCardDesignCapabilities = experiments.enabled(
    SPECS.GiftCardDesignCapabilities,
  );

  const [mobileWidth, setMobileWidth] = useReactState(
    DEFAULT_IMAGE_WIDTH_MOBILE,
  );

  const imageMedia = selectedVariant?.media || product?.media;
  const imageRatioFromSettings = convertRatioParamToRatioValue(
    styles.get(stylesParams.imageRatio),
  );
  const aspectRatio = imageMedia && imageMedia.width / imageMedia.height;

  useEffect(() => {
    if (!isMobile) {
      return;
    }

    const updateMobileWidth = () => {
      if (containerRef.current) {
        const newWidth = containerRef.current.offsetWidth;
        if (newWidth > 0) {
          setMobileWidth(newWidth);
        }
      }
    };

    updateMobileWidth();
    window.addEventListener('resize', updateMobileWidth);

    return () => {
      window.removeEventListener('resize', updateMobileWidth);
    };
  }, [isMobile, setMobileWidth]);

  const DefaultImage = () => {
    return (
      <div
        data-hook={ProductImageDataHook.defaultImage}
        className={cx(
          s.defaultImage,
          shouldUseNewGiftCardDesignCapabilities
            ? s.imageContainer
            : s.imageContainerOld,
          isMobile && s.defaultImageMobile,
        )}
      >
        <DefaultImageIcon />
      </div>
    );
  };

  return product && imageMedia?.url ? (
    <div ref={containerRef}>
      <Image
        data-hook={ProductImageDataHook.image}
        className={
          shouldUseNewGiftCardDesignCapabilities
            ? s.imageContainer
            : s.imageContainerOld
        }
        key={imageMedia.url}
        src={imageMedia.url}
        width={isMobile ? mobileWidth : DEFAULT_IMAGE_WIDTH_DESKTOP}
        alt={imageMedia.alt_image}
        resize={
          shouldUseNewGiftCardDesignCapabilities
            ? ImageResizeOptions.cover
            : ImageResizeOptions.contain
        }
        aspectRatio={
          shouldUseNewGiftCardDesignCapabilities
            ? imageRatioFromSettings || aspectRatio
            : aspectRatio
        }
        loadingBehavior={ImageLoadingBehaviorOptions.blur}
        hoverEffect={ImageHoverEffectOptions.zoom}
      />
    </div>
  ) : (
    <DefaultImage />
  );
};

export default ProductImage;
